"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import type { ThetaEvent } from "@/lib/hooks/useEvents";
import { MONTH_LONG, formatTime } from "@/lib/dates";

// The weekday column headers, Monday first — a grid label, not a date. The
// long Sunday-first names `lib/dates.ts` exports are a different list for a
// different job.
const DAY_NAMES = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const DAY_NAMES_SHORT = ["Ma", "Ti", "On", "To", "Fr", "Lø", "Sø"];

interface CalendarViewProps {
  events: ThetaEvent[];
  year: number;
  month: number;
  onMonthChange: (year: number, month: number) => void;
  isLoading?: boolean;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  // 0=Sun, convert to Mon=0
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

export function CalendarView({
  events,
  year,
  month,
  onMonthChange,
  isLoading,
}: CalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() + 1 === month;

  // Group events by day
  const eventsByDay = useMemo(() => {
    const map = new Map<number, ThetaEvent[]>();
    for (const event of events) {
      const d = new Date(event.startsAt);
      if (d.getFullYear() === year && d.getMonth() + 1 === month) {
        const day = d.getDate();
        if (!map.has(day)) map.set(day, []);
        map.get(day)!.push(event);
      }
    }
    return map;
  }, [events, year, month]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const prevMonth = () => {
    if (month === 1) onMonthChange(year - 1, 12);
    else onMonthChange(year, month - 1);
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (month === 12) onMonthChange(year + 1, 1);
    else onMonthChange(year, month + 1);
    setSelectedDate(null);
  };

  const goToToday = () => {
    onMonthChange(today.getFullYear(), today.getMonth() + 1);
    setSelectedDate(today.getDate());
  };

  const selectedEvents = selectedDate ? eventsByDay.get(selectedDate) ?? [] : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="p-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
          aria-label="Forrige måned"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-foreground capitalize">
            {MONTH_LONG[month - 1]} {year}
          </h2>
          {!isCurrentMonth && (
            <button
              onClick={goToToday}
              className="text-xs text-primary hover:underline"
            >
              I dag
            </button>
          )}
        </div>
        <button
          onClick={nextMonth}
          className="p-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
          aria-label="Næste måned"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-px bg-muted rounded-lg overflow-hidden border border-border">
        {DAY_NAMES.map((name, i) => (
          <div
            key={name}
            className="bg-surface-2 text-center text-xs font-medium text-muted-foreground py-2"
          >
            <span className="hidden sm:inline">{name}</span>
            <span className="sm:hidden">{DAY_NAMES_SHORT[i]}</span>
          </div>
        ))}

        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="bg-surface min-h-[56px] sm:min-h-[80px] md:min-h-[100px]" />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayEvents = eventsByDay.get(day) ?? [];
          const isToday = isCurrentMonth && today.getDate() === day;
          const isSelected = selectedDate === day;

          return (
            <button
              key={day}
              onClick={() => setSelectedDate(isSelected ? null : day)}
              className={`bg-surface min-h-[56px] sm:min-h-[80px] md:min-h-[100px] p-0.5 sm:p-1 md:p-1.5 text-left transition-colors relative ${
                isSelected
                  ? "ring-2 ring-inset ring-indigo-500"
                  : dayEvents.length > 0
                    ? "hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                    : "hover:bg-surface-2/50"
              }`}
            >
              <span
                className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
                  isToday
                    ? "bg-indigo-600 text-white"
                    : "text-foreground"
                }`}
              >
                {day}
              </span>
              {/* Event dots on mobile, previews on larger screens */}
              <div className="mt-0.5 space-y-0.5 overflow-hidden">
                <div className="flex gap-0.5 sm:hidden mt-1">
                  {dayEvents.slice(0, 4).map((event) => (
                    <span key={event.id} className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  ))}
                </div>
                {dayEvents.slice(0, 3).map((event) => (
                  <div
                    key={event.id}
                    className="hidden sm:block text-[10px] sm:text-xs leading-tight truncate px-1 py-0.5 rounded bg-primary-muted/40 text-primary-muted-foreground"
                  >
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="hidden sm:block text-[10px] text-subtle-foreground px-1">
                    +{dayEvents.length - 3} mere
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div className="mt-4 text-center text-sm text-subtle-foreground">
          Indlæser begivenheder...
        </div>
      )}

      {selectedDate !== null && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-2 capitalize">
            {selectedDate}. {MONTH_LONG[month - 1]}
          </h3>
          {selectedEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ingen begivenheder denne dag.
            </p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((event) => {
                const time = formatTime(event.startsAt);
                return (
                  <Link
                    key={event.id}
                    href={`/begivenheder/${event.id}`}
                    className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-surface-2 transition-colors"
                  >
                    <div className="shrink-0 w-12 text-center">
                      <div className="text-sm font-semibold text-primary">
                        {time}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-foreground text-sm truncate">
                        {event.title}
                      </h4>
                      {event.location && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          {event.location}
                        </p>
                      )}
                      <p className="text-xs text-subtle-foreground mt-0.5">
                        {event.rsvpSummary.goingCount > 0 &&
                          `${event.rsvpSummary.goingCount} ${event.rsvpSummary.goingCount === 1 ? "deltager" : "deltagere"}`}
                        {event.rsvpSummary.goingCount > 0 && event.groups.length > 0 && " · "}
                        {event.groups.map((g) => g.name).join(", ")}
                      </p>
                    </div>
                    {/* The same badge, and the same rule as everywhere else:
                        `isPubliclyVisible`, never `!isPrivate`. */}
                    {event.isPubliclyVisible && (
                      <span className="shrink-0 text-[10px] font-bold text-green-600 dark:text-green-400 uppercase">
                        Offentlig
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
